import React from 'react';
import { Button } from '../../ui/button';
import { Coffee, Sun, Moon, Cookie, Utensils } from 'lucide-react';

// Default meal times (used if no custom ones provided)
const DEFAULT_MEAL_TIMES = [
  { id: 'breakfast', name: 'Breakfast', icon: '🌅', color: 'from-amber-500 to-orange-500', enabled: true },
  { id: 'lunch', name: 'Lunch', icon: '☀️', color: 'from-yellow-500 to-amber-500', enabled: true },
  { id: 'dinner', name: 'Dinner', icon: '🌙', color: 'from-indigo-500 to-purple-500', enabled: true },
  { id: 'snack', name: 'Snack', icon: '🍎', color: 'from-pink-500 to-rose-500', enabled: false },
];

// Icon mapping for default meals
const ICON_MAP = {
  breakfast: Coffee,
  lunch: Sun,
  dinner: Moon,
  snack: Cookie,
  snacks: Cookie,
};

// Color mapping for meals
const COLOR_MAP = {
  breakfast: 'from-amber-500 to-orange-500',
  lunch: 'from-yellow-500 to-amber-500',
  dinner: 'from-indigo-500 to-purple-500',
  snack: 'from-pink-500 to-rose-500',
  snacks: 'from-pink-500 to-rose-500',
};

// Get user's meal times from localStorage or use defaults
const getUserMealTimes = (customMealTimes) => {
  // If custom meal times provided as prop, use them
  // Filter only enabled meals, or assume enabled if property is missing
  if (customMealTimes && customMealTimes.length > 0) {
    return customMealTimes.filter(m => m.enabled !== false);
  }
  
  // Try to get from localStorage
  try {
    const saved = localStorage.getItem('gym_notebook_meal_times');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.filter(m => m.enabled !== false);
    }
  } catch (e) {
    console.log('Error loading meal times');
  }
  
  // Return defaults (only enabled ones)
  return DEFAULT_MEAL_TIMES.filter(m => m.enabled !== false);
};

export function MealTimeSelector({ selectedMealTime, onMealTimeChange, compact = false, mealTimes: customMealTimes }) {
  const mealTimes = getUserMealTimes(customMealTimes);
  
  if (compact) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {mealTimes.map((meal) => {
          const Icon = ICON_MAP[meal.id] || Utensils;
          const color = COLOR_MAP[meal.id] || 'from-slate-500 to-slate-600';
          const isSelected = selectedMealTime === meal.id;
          return (
            <Button
              key={meal.id}
              type="button"
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => onMealTimeChange(meal.id)}
              className={`h-8 px-2 ${isSelected 
                ? `bg-gradient-to-r ${color} border-0 text-white` 
                : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
              }`}
            >
              {ICON_MAP[meal.id] ? (
                <Icon className="h-3.5 w-3.5 mr-1" />
              ) : (
                <span className="mr-1">{meal.icon || '🍽️'}</span>
              )}
              <span className="text-xs">{meal.name}</span>
            </Button>
          );
        })}
      </div>
    );
  }

  // Dynamic grid based on number of meals
  const gridCols = mealTimes.length <= 3 ? 'grid-cols-3' : 
                   mealTimes.length === 4 ? 'grid-cols-4' : 
                   'grid-cols-3 sm:grid-cols-4';

  return (
    <div className={`grid ${gridCols} gap-2`}>
      {mealTimes.map((meal) => {
        const Icon = ICON_MAP[meal.id] || Utensils;
        const color = COLOR_MAP[meal.id] || 'from-slate-500 to-slate-600';
        const isSelected = selectedMealTime === meal.id;
        return (
          <Button
            key={meal.id}
            type="button"
            variant={isSelected ? "default" : "outline"}
            onClick={() => onMealTimeChange(meal.id)}
            className={`flex flex-col items-center py-3 h-auto ${isSelected 
              ? `bg-gradient-to-r ${color} border-0 text-white` 
              : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
            }`}
          >
            {ICON_MAP[meal.id] ? (
              <Icon className={`h-5 w-5 mb-1 ${isSelected ? 'text-white' : ''}`} />
            ) : (
              <span className="text-lg mb-1">{meal.icon || '🍽️'}</span>
            )}
            <span className="text-xs">{meal.name}</span>
          </Button>
        );
      })}
    </div>
  );
}

export default MealTimeSelector;
export { DEFAULT_MEAL_TIMES, getUserMealTimes };
